import { useEffect, useState } from "react";
import { Users, UserCheck, Clock, UserX, CalendarOff, LogOut, PartyPopper, AlertTriangle, HeartPulse, UserMinus, Ban, Briefcase } from "lucide-react";
import { api, fmtTime } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";

const CARDS = [
  ["present", "Present", UserCheck, "text-emerald-600", "stats-card-present-count"],
  ["late", "Late", Clock, "text-amber-600", "stats-card-late-count"],
  ["absent", "Absent", UserX, "text-rose-600", "stats-card-absent-count"],
  ["weekly_off", "Weekly Off", CalendarOff, "text-indigo-600", "stats-card-weekly-off-count"],
  ["checked_out", "Checked Out", LogOut, "text-sky-600", "stats-card-checked-out-count"],
  ["sick_leave", "Sick Leave Today", HeartPulse, "text-fuchsia-600", "stats-card-sick-leave-count"],
];

const WORKFORCE = [
  ["all_workers", "Total Workers", Users, "text-slate-900", "stats-card-all-workers-count"],
  ["total", "Active", Briefcase, "text-emerald-600", "stats-card-total-count"],
  ["inactive", "Non-active", UserX, "text-slate-500", "stats-card-inactive-count"],
  ["escaped", "Escaped", UserMinus, "text-orange-600", "stats-card-escaped-count"],
  ["terminated", "Terminated", Ban, "text-rose-700", "stats-card-terminated-count"],
];

const CardGrid = ({ title, cards, stats, testId }) => (
  <section className="space-y-3" data-testid={testId}>
    <h3 className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-slate-500">{title}</h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
      {cards.map(([k, label, Icon, tone, tid], i) => (
        <div key={k} className="rise bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow" style={{ animationDelay: `${i * 50}ms` }}>
          <div className="flex items-center justify-between text-slate-500"><span className="text-xs font-medium">{label}</span><Icon size={16} /></div>
          <p data-testid={tid} className={`font-mono text-4xl font-bold mt-3 tabular-nums ${tone}`}>{stats ? stats[k] : "–"}</p>
        </div>
      ))}
    </div>
  </section>
);

const PersonList = ({ title, items, tone, testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
    <h3 className={`font-mono text-xs font-bold tracking-[0.2em] uppercase ${tone}`}>{title} · {items.length}</h3>
    <div className="mt-3 space-y-2 max-h-56 overflow-y-auto">
      {items.length === 0 && <p className="text-sm text-slate-400">None</p>}
      {items.map((w) => (
        <div key={w.id} className="flex justify-between text-sm"><span className="text-slate-800">{w.name}</span><span className="font-mono text-slate-400">{w.code}</span></div>
      ))}
    </div>
  </div>
);

const ExpiryAlerts = ({ items }) => (
  <div className="bg-white border border-amber-200 rounded-xl p-5" data-testid="dashboard-expiry-alerts">
    <h3 className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-amber-700 flex items-center gap-2"><AlertTriangle size={14} /> Expiring ≤ 30 days · {items.length}</h3>
    <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
      {items.length === 0 && <p className="text-sm text-slate-400">No Ram Pass or Iqama expiring soon.</p>}
      {items.map((a) => (
        <div key={a.id} data-testid={`expiry-alert-${a.id}`} className="flex items-center justify-between gap-2 text-sm border-b border-slate-100 pb-2">
          <div className="min-w-0"><p className="text-slate-800 truncate">{a.name} <span className="font-mono text-xs text-slate-400">{a.code}</span></p><p className="text-xs text-slate-500">{a.doc}{a.number ? ` · ${a.number}` : ""} · {a.expiry}</p></div>
          <span className={`shrink-0 px-2 py-0.5 rounded-full border font-mono text-[11px] font-bold ${a.days_left < 0 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {a.days_left < 0 ? "EXPIRED" : `${a.days_left}d`}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/dashboard/live").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <PageHeader overline={data ? `${data.weekday} · ${data.date}` : "Loading"} title="Live Overview">
        <span className="flex items-center gap-2 text-xs font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5" data-testid="dashboard-live-indicator">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
          LIVE · refresh 3s
        </span>
      </PageHeader>

      {data?.holiday && (
        <div data-testid="dashboard-holiday-banner" className="rise flex items-center gap-3 bg-sky-50 border border-sky-200 text-sky-800 rounded-xl px-5 py-4">
          <PartyPopper size={18} /> <span>Today is a company holiday: <strong>{data.holiday}</strong>. No one is marked absent.</span>
        </div>
      )}

      <CardGrid title="Workforce" cards={WORKFORCE} stats={data?.stats} testId="dashboard-workforce-section" />
      <CardGrid title="Today's attendance" cards={CARDS} stats={data?.stats} testId="dashboard-today-section" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex justify-between items-center">
            <h3 className="font-display text-lg font-bold">Today's punches</h3>
            <span className="font-mono text-xs text-slate-400">{data?.feed.length ?? 0} records</span>
          </div>
          <div className="overflow-x-auto max-h-[520px]">
            <table className="w-full text-sm" data-testid="dashboard-live-feed">
              <thead className="text-left text-xs uppercase text-slate-500 font-mono bg-slate-50">
                <tr><th className="px-5 py-3">Worker</th><th className="px-5 py-3">In</th><th className="px-5 py-3">Out</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Hours</th></tr>
              </thead>
              <tbody>
                {data?.feed.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">No attendance yet today.</td></tr>}
                {data?.feed.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 rise" data-testid={`feed-row-${r.id}`}>
                    <td className="px-5 py-3"><p className="font-medium text-slate-900">{r.worker_name}</p><p className="font-mono text-xs text-slate-400">{r.worker_code} · {r.department || "—"}</p></td>
                    <td className="px-5 py-3 font-mono">{fmtTime(r.check_in)}</td>
                    <td className="px-5 py-3 font-mono">{fmtTime(r.check_out)}</td>
                    <td className="px-5 py-3"><StatusBadge status={r.status} />{r.late_minutes > 0 && <span className="ml-2 text-xs text-amber-600 font-mono">+{r.late_minutes}m</span>}</td>
                    <td className="px-5 py-3 font-mono">{r.check_out ? r.hours : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lg:col-span-4 space-y-6">
          <ExpiryAlerts items={data?.expiry_alerts || []} />
          <PersonList title="Absent" items={data?.absent_list || []} tone="text-rose-600" testId="dashboard-absent-list" />
          <PersonList title="On sick leave" items={data?.sick_list || []} tone="text-fuchsia-600" testId="dashboard-sick-list" />
          <PersonList title="On weekly off" items={data?.off_list || []} tone="text-indigo-600" testId="dashboard-off-list" />
        </div>
      </div>
    </>
  );
}
